/**
 * Consolidated status constants and helpers for packages and pickups.
 * Single source of truth for status logic per workflow optimization specs.
 */

// =============================================================================
// PACKAGE STATUSES (Consolidated)
// =============================================================================

export const PACKAGE_STATUS = {
  PENDING: "pending",
  PICKED_UP: "picked_up",
  DELIVERED: "delivered",
  RETURNED: "returned",
  ARCHIVED: "archived",
  USER_CANCELED: "user_canceled",
  RESUBMITTED: "resubmitted",
  SCHEDULED: "scheduled",
} as const;

export const PACKAGE_APPROVAL_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

// =============================================================================
// USER PICKUP STATUSES (Consolidated)
// =============================================================================

export const PICKUP_STATUS = {
  // User-initiated, awaiting user to finalize
  AWAITING_CONFIRMATION: "awaiting_confirmation",
  // User confirmed, awaiting admin/manager approval
  AWAITING_ADMIN_CONFIRMATION: "awaiting_admin_confirmation",
  // Admin counter-proposed new time
  COUNTER_PROPOSED: "counter_proposed",
  // Final confirmed pickup
  CONFIRMED: "confirmed",
  // Pickup completed successfully
  COMPLETED: "completed",
  // Edge cases
  MISSED: "missed",
  NO_SHOW: "no_show",
  CANCELLED: "cancelled",
  AUTO_CANCELED: "auto_canceled",
  USER_DENIED: "user_denied",
} as const;

// =============================================================================
// STATUS GROUPINGS FOR QUERIES
// =============================================================================

/** Statuses that count as "active" packages (eligible for pickup) */
export const ACTIVE_PACKAGE_STATUSES = [
  PACKAGE_STATUS.PENDING,
  PACKAGE_STATUS.SCHEDULED,
] as const;

/** Approval statuses that count as "active" */
export const ACTIVE_APPROVAL_STATUSES = [
  PACKAGE_APPROVAL_STATUS.PENDING,
  PACKAGE_APPROVAL_STATUS.APPROVED,
] as const;

/** Package statuses that belong in history view */
export const HISTORY_PACKAGE_STATUSES = [
  PACKAGE_STATUS.PICKED_UP,
  PACKAGE_STATUS.RETURNED,
  PACKAGE_STATUS.DELIVERED,
  PACKAGE_STATUS.ARCHIVED,
  PACKAGE_STATUS.USER_CANCELED,
  PACKAGE_STATUS.RESUBMITTED,
] as const;

/** Package statuses that allow cancellation */
export const CANCELLABLE_PACKAGE_STATUSES = [
  PACKAGE_STATUS.PENDING,
  PACKAGE_STATUS.SCHEDULED,
] as const;

/** Statuses that block cancellation */
export const NON_CANCELLABLE_PACKAGE_STATUSES = [
  PACKAGE_STATUS.PICKED_UP,
  PACKAGE_STATUS.DELIVERED,
  PACKAGE_STATUS.RETURNED,
  PACKAGE_STATUS.USER_CANCELED,
  PACKAGE_STATUS.ARCHIVED,
] as const;

/** Pickup statuses that are considered "active" (in progress) */
export const ACTIVE_PICKUP_STATUSES = [
  PICKUP_STATUS.AWAITING_CONFIRMATION,
  PICKUP_STATUS.AWAITING_ADMIN_CONFIRMATION,
  PICKUP_STATUS.COUNTER_PROPOSED,
  PICKUP_STATUS.CONFIRMED,
] as const;

/** Pickup statuses that count toward monthly limit */
export const COUNTING_PICKUP_STATUSES = [
  PICKUP_STATUS.CONFIRMED,
  PICKUP_STATUS.COMPLETED,
  PICKUP_STATUS.NO_SHOW,
] as const;

/** Pickup statuses that do NOT count toward monthly limit */
export const NON_COUNTING_PICKUP_STATUSES = [
  PICKUP_STATUS.MISSED,
  PICKUP_STATUS.CANCELLED,
  PICKUP_STATUS.AUTO_CANCELED,
  PICKUP_STATUS.USER_DENIED,
] as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a package status qualifies as "active" (not in history)
 */
export function isActivePackage(status: string, approvalStatus: string): boolean {
  return (
    ACTIVE_PACKAGE_STATUSES.includes(status as any) &&
    ACTIVE_APPROVAL_STATUSES.includes(approvalStatus as any)
  );
}

/**
 * Check if a package belongs in history view
 */
export function isHistoryPackage(status: string, approvalStatus: string): boolean {
  return (
    HISTORY_PACKAGE_STATUSES.includes(status as any) ||
    approvalStatus === PACKAGE_APPROVAL_STATUS.REJECTED
  );
}

/**
 * Check if a package can be cancelled by the user
 */
export function canCancelPackage(status: string, approvalStatus: string): boolean {
  if (NON_CANCELLABLE_PACKAGE_STATUSES.includes(status as any)) {
    return false;
  }
  return (
    CANCELLABLE_PACKAGE_STATUSES.includes(status as any) &&
    ACTIVE_APPROVAL_STATUSES.includes(approvalStatus as any)
  );
}

/**
 * Check if a pickup is in an active state
 */
export function isActivePickup(status: string): boolean {
  return ACTIVE_PICKUP_STATUSES.includes(status as any);
}

/**
 * Check if a pickup counts toward monthly limit
 */
export function pickupCountsTowardLimit(status: string): boolean {
  return COUNTING_PICKUP_STATUSES.includes(status as any);
}

/**
 * Get display label for pickup status
 */
export function getPickupStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    [PICKUP_STATUS.AWAITING_CONFIRMATION]: "Awaiting Your Confirmation",
    [PICKUP_STATUS.AWAITING_ADMIN_CONFIRMATION]: "Awaiting Confirmation",
    [PICKUP_STATUS.COUNTER_PROPOSED]: "New Time Proposed",
    [PICKUP_STATUS.CONFIRMED]: "Scheduled",
    [PICKUP_STATUS.COMPLETED]: "Completed",
    [PICKUP_STATUS.MISSED]: "Missed",
    [PICKUP_STATUS.NO_SHOW]: "No-Show",
    [PICKUP_STATUS.CANCELLED]: "Cancelled",
    [PICKUP_STATUS.AUTO_CANCELED]: "Auto-Cancelled",
    [PICKUP_STATUS.USER_DENIED]: "Denied",
  };
  return labels[status] || status;
}

/**
 * Get display label for package status
 */
export function getPackageStatusLabel(status: string, approvalStatus: string): string {
  if (approvalStatus === PACKAGE_APPROVAL_STATUS.REJECTED) {
    return "Rejected";
  }
  if (approvalStatus === PACKAGE_APPROVAL_STATUS.PENDING) {
    return "Pending Review";
  }
  
  const labels: Record<string, string> = {
    [PACKAGE_STATUS.PENDING]: "Awaiting Pickup",
    [PACKAGE_STATUS.SCHEDULED]: "Scheduled",
    [PACKAGE_STATUS.PICKED_UP]: "Picked Up",
    [PACKAGE_STATUS.DELIVERED]: "Delivered",
    [PACKAGE_STATUS.RETURNED]: "Returned",
    [PACKAGE_STATUS.ARCHIVED]: "Archived",
    [PACKAGE_STATUS.USER_CANCELED]: "Cancelled",
    [PACKAGE_STATUS.RESUBMITTED]: "Resubmitted",
  };
  return labels[status] || status;
}

/**
 * Derive the pickup timeline stage from current state
 */
export type PickupTimelineStage = "submitted" | "approved" | "scheduled" | "picked_up" | "returned";

export function derivePickupStage(
  pickupStatus: string | null,
  hasApprovedPackages: boolean,
  hasPendingPackages: boolean,
  hasPickedUpPackages: boolean,
  hasReturnedPackages: boolean
): PickupTimelineStage | null {
  // Returned/delivered takes priority
  if (hasReturnedPackages) return "returned";
  
  // Picked up is next
  if (hasPickedUpPackages) return "picked_up";
  
  // Scheduled pickup
  if (pickupStatus && ACTIVE_PICKUP_STATUSES.includes(pickupStatus as any)) {
    return "scheduled";
  }
  
  // Approved packages but no pickup scheduled
  if (hasApprovedPackages) return "approved";
  
  // Only pending packages
  if (hasPendingPackages) return "submitted";
  
  // No active packages
  return null;
}
